import React, { useEffect } from 'react';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Sheet from '@amzn/meridian/sheet';
import Text from '@amzn/meridian/text';
import { getAssociateImageQuality, logImageQualityAction } from '../imageQuality/api';
import { ASSOCIATE_SCORECARD_DRAWER_OPEN } from '../../utils/metric-utils';
import { GetAssociateImageQualityOutput, ReportWindow } from '../imageQuality/model';
import {
  formatCycleTime,
  formatPct,
  passRatePct,
  qualityTier,
} from '../imageQuality/imageQualityUtils';
import { useReportFetch, reportRequestKey } from '../imageQuality/useReportFetch';
import FailureBreakdown from '../imageQuality/FailureBreakdown';
import PhotoGrid from '../imageQuality/PhotoGrid';
import { Label, LoadFailed, textColorForTier } from '../imageQuality/meridianUi';

const DRAWER_WIDTH = 640;
// Three photos per row on Meridian's 12-column grid.
const PHOTO_GRID_WIDTH = 'grid-4';

const formatRate = (pct?: number): string => (pct === undefined ? '—' : formatPct(pct));

export interface AssociateDrillInProps {
  warehouseId: string;
  reportWindow: ReportWindow;
  /** null = closed. */
  login: string | null;
  onClose: () => void;
  /** Opens this associate on the Image Quality page (all photos). */
  onOpenImageQuality: (login: string) => void;
}

/** Side drawer for one associate: stats, failure breakdown and recent photos, worst first. */
const AssociateDrillIn: React.FC<AssociateDrillInProps> = ({
  warehouseId,
  reportWindow,
  login,
  onClose,
  onOpenImageQuality,
}) => {
  // The drawer stays mounted while closed (so it can animate); it only fetches once a login is set.
  const openLogin = login ?? '';
  const {
    data: detail,
    loading,
    failed,
    retry,
  } = useReportFetch(
    () => getAssociateImageQuality({ warehouseId, login: openLogin, ...reportWindow }),
    reportRequestKey(reportWindow, warehouseId, openLogin),
    !login
  );

  useEffect(() => {
    if (login) logImageQualityAction(ASSOCIATE_SCORECARD_DRAWER_OPEN);
  }, [login]);

  return (
    <Sheet
      open={!!login}
      onClose={onClose}
      side="end"
      type="overlay"
      spacingInset="none"
      data-testid="scorecard-drill-in"
    >
      <div style={{ width: DRAWER_WIDTH }}>
        <Column spacing="none">
          <Row alignmentHorizontal="justify" alignmentVertical="top" spacingInset="500">
            <DrawerHeader login={login ?? ''} warehouseId={warehouseId} detail={detail} />
            <Button
              type="icon"
              onClick={onClose}
              label="Close"
              data-testid="scorecard-drill-in-close"
            >
              ✕
            </Button>
          </Row>
          <Divider />
          {loading && (
            <Row alignmentHorizontal="center" spacingInset="500">
              <Loader />
            </Row>
          )}
          {failed && (
            <LoadFailed
              message="Couldn't load this associate's photos."
              onRetry={retry}
              testId="scorecard-drill-in-failed"
            />
          )}
          {detail && login && (
            <DrawerBody
              detail={detail}
              warehouseId={warehouseId}
              onViewAll={() => onOpenImageQuality(login)}
            />
          )}
        </Column>
      </div>
    </Sheet>
  );
};

const DrawerHeader: React.FC<{
  login: string;
  warehouseId: string;
  detail: GetAssociateImageQualityOutput | null;
}> = ({ login, warehouseId, detail }) => {
  const pct = detail
    ? passRatePct(detail.associate.passingPhotos, detail.associate.totalPhotos)
    : null;
  const tier = detail ? qualityTier(pct, detail.thresholds) : null;
  return (
    <Column spacing="100">
      <Heading level={2} type="h500">
        <Text tag="span" type="h500" fontFamily="amazonEmberMonospace">
          {login}
        </Text>
      </Heading>
      <Text type="b400" color="secondary">
        {`${warehouseId} · Image quality: `}
        <Text
          tag="span"
          type="h300"
          color={textColorForTier(tier)}
          data-testid="scorecard-drill-in-quality"
        >
          {formatPct(pct)}
        </Text>
      </Text>
    </Column>
  );
};

const DrawerBody: React.FC<{
  detail: GetAssociateImageQualityOutput;
  warehouseId: string;
  onViewAll: () => void;
}> = ({ detail, warehouseId, onViewAll }) => {
  const { associate, failureBreakdown, photos } = detail;
  const failing = associate.totalPhotos - associate.passingPhotos;
  const stats: { key: string; label: string; value: string; color?: 'error' }[] = [
    { key: 'photos', label: 'Photos', value: `${associate.totalPhotos}` },
    { key: 'passing', label: 'Passing', value: `${associate.passingPhotos}` },
    {
      key: 'failing',
      label: 'Failing',
      value: `${failing}`,
      color: failing > 0 ? 'error' : undefined,
    },
    { key: 'retake', label: 'Retake', value: formatRate(associate.retakeRatePct) },
    { key: 'cycle', label: 'Cycle', value: formatCycleTime(associate.avgCycleTimeSeconds) },
    { key: 'skip', label: 'Skip', value: formatRate(associate.skipRatePct) },
  ];
  return (
    <>
      <Row spacing="300" wrap="down" spacingInset="500">
        {stats.map(stat => (
          <Card
            key={stat.key}
            spacingInset="300 400"
            data-testid={`scorecard-drill-in-stat-${stat.key}`}
          >
            <Column spacing="100" alignmentHorizontal="center">
              <Text type="h500" color={stat.color}>
                {stat.value}
              </Text>
              <Text type="h100" color="secondary">
                {stat.label.toUpperCase()}
              </Text>
            </Column>
          </Card>
        ))}
      </Row>
      <Divider />
      <Column spacing="400" spacingInset="500">
        <Label>Failure breakdown</Label>
        <FailureBreakdown breakdown={failureBreakdown} scaleTo={associate.totalPhotos} />
      </Column>
      <Divider />
      <Column spacing="400" spacingInset="500">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Label count={`${photos.length} of ${associate.totalPhotos}`}>
            Recent photos — worst first
          </Label>
          <Link onClick={onViewAll} data-testid="scorecard-drill-in-view-all">
            View all on Image Quality
          </Link>
        </Row>
        <PhotoGrid warehouseId={warehouseId} photos={photos} cardWidth={PHOTO_GRID_WIDTH} />
      </Column>
    </>
  );
};

export default AssociateDrillIn;
